import { Link } from 'react-router-dom';
import EntityLink from '@/components/Entity/EntityLink';
import TradeLogicV2ExitSummary from '@/components/TradeLogic/TradeLogicV2ExitSummary';
import { calculateRiskAdjustedScore, calculateProfitWeightedScore } from '@einhaus/project314-shared';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type {
    Entity,
    TradingStrategy,
    OptionBuyingStrategy,
    TradingStrategyBacktest as SharedTradingStrategyBacktest
} from '@einhaus/project314-types';
import type { TradingStrategyBacktest } from '@/types/appRouter';

export interface ColumnConfig {
    id: string;
    label: string;
    defaultVisible: boolean;
    category: 'basic' | 'performance' | 'trailing' | 'advanced' | 'strategy';
    group?: string;
}

export interface ColumnGroup {
    id: string;
    label: string;
    columns: ColumnConfig[];
}

// Define all possible columns with metadata
export const columnRegistry: ColumnConfig[] = [
    // Basic Info
    { id: 'id', label: 'Backtest ID', defaultVisible: true, category: 'basic' },
    { id: 'entityId', label: 'Entity', defaultVisible: true, category: 'basic' },
    { id: 'strategyId', label: 'Strategy ID', defaultVisible: false, category: 'basic' },
    { id: 'startDateTime', label: 'Start Date', defaultVisible: false, category: 'basic' },
    { id: 'endDateTime', label: 'End Date', defaultVisible: false, category: 'basic' },

    // Strategy Info
    { id: 'optionStrategies', label: 'Option Strategies', defaultVisible: false, category: 'strategy' },
    { id: 'exitLogic', label: 'Trade Logic V2 Exit', defaultVisible: false, category: 'strategy' },

    // Performance Metrics
    { id: 'avgTradeProfit', label: 'Avg Return %', defaultVisible: true, category: 'performance' },
    { id: 'trades', label: 'Num Trades', defaultVisible: true, category: 'performance' },
    { id: 'winRate', label: 'Win Rate', defaultVisible: true, category: 'performance' },
    { id: 'riskAdjustedScore', label: 'Risk Score', defaultVisible: true, category: 'performance' },
    { id: 'profitWeightedScore', label: 'Profit Score', defaultVisible: true, category: 'performance' },
    { id: 'profit', label: 'Profit', defaultVisible: false, category: 'performance' },
    { id: 'profitPercentage', label: 'Profit %', defaultVisible: true, category: 'performance' },
    { id: 'medianProfitPercent', label: 'Median trade %', defaultVisible: true, category: 'performance' },
    { id: 'medianProfitPerDayPercent', label: 'Median profit/day %', defaultVisible: false, category: 'performance' },
    { id: 'avgProfitPerDayPercent', label: 'Avg profit/day %', defaultVisible: false, category: 'performance' },
    { id: 'returnOnRiskCapital', label: 'Return on risk %', defaultVisible: false, category: 'performance' },
    { id: 'profitFactor', label: 'Profit Factor', defaultVisible: false, category: 'performance' },
    { id: 'distinctTradingDays', label: 'Trading Days', defaultVisible: false, category: 'performance' },
    { id: 'activeMonths', label: 'Active Months', defaultVisible: false, category: 'performance' },
    { id: 'worstMonth', label: 'Worst Month', defaultVisible: false, category: 'performance' },

    // 7-Day Trailing
    { id: 'trailingStats.avgGain7', label: 'Avg Profit 7d', defaultVisible: false, category: 'trailing', group: '7d' },
    { id: 'trailingStats.numTrades7', label: 'Num Trades 7d', defaultVisible: false, category: 'trailing', group: '7d' },
    { id: 'trailingStats.winRate7', label: 'Win Rate 7d', defaultVisible: false, category: 'trailing', group: '7d' },

    // 14-Day Trailing
    { id: 'trailingStats.avgGain14', label: 'Avg Profit 14d', defaultVisible: false, category: 'trailing', group: '14d' },
    { id: 'trailingStats.numTrades14', label: 'Num Trades 14d', defaultVisible: false, category: 'trailing', group: '14d' },
    { id: 'trailingStats.winRate14', label: 'Win Rate 14d', defaultVisible: false, category: 'trailing', group: '14d' },

    // 30-Day Trailing
    { id: 'trailingStats.avgGain30', label: 'Avg Profit 30d', defaultVisible: false, category: 'trailing', group: '30d' },
    { id: 'trailingStats.numTrades30', label: 'Num Trades 30d', defaultVisible: false, category: 'trailing', group: '30d' },
    { id: 'trailingStats.winRate30', label: 'Win Rate 30d', defaultVisible: false, category: 'trailing', group: '30d' },

    // 60-Day Trailing
    { id: 'trailingStats.avgGain60', label: 'Avg Profit 60d', defaultVisible: false, category: 'trailing', group: '60d' },
    { id: 'trailingStats.numTrades60', label: 'Num Trades 60d', defaultVisible: false, category: 'trailing', group: '60d' },
    { id: 'trailingStats.winRate60', label: 'Win Rate 60d', defaultVisible: false, category: 'trailing', group: '60d' },

    // 90-Day Trailing
    { id: 'trailingStats.avgGain90', label: 'Avg Profit 90d', defaultVisible: false, category: 'trailing', group: '90d' },
    { id: 'trailingStats.numTrades90', label: 'Num Trades 90d', defaultVisible: false, category: 'trailing', group: '90d' },
    { id: 'trailingStats.winRate90', label: 'Win Rate 90d', defaultVisible: false, category: 'trailing', group: '90d' },

    // 365-Day Trailing
    { id: 'trailingStats.avgGain365', label: 'Avg Profit 365d', defaultVisible: false, category: 'trailing', group: '365d' },
    { id: 'trailingStats.numTrades365', label: 'Num Trades 365d', defaultVisible: false, category: 'trailing', group: '365d' },
    { id: 'trailingStats.winRate365', label: 'Win Rate 365d', defaultVisible: false, category: 'trailing', group: '365d' }
];

// Group columns for UI organization
export const columnGroups: ColumnGroup[] = [
    {
        id: 'basic',
        label: 'Basic Info',
        columns: columnRegistry.filter((col) => col.category === 'basic')
    },
    {
        id: 'strategy',
        label: 'Strategy Details',
        columns: columnRegistry.filter((col) => col.category === 'strategy')
    },
    {
        id: 'performance',
        label: 'Performance Metrics',
        columns: columnRegistry.filter((col) => col.category === 'performance')
    },
    {
        id: 'trailing-7d',
        label: '7-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '7d')
    },
    {
        id: 'trailing-14d',
        label: '14-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '14d')
    },
    {
        id: 'trailing-30d',
        label: '30-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '30d')
    },
    {
        id: 'trailing-60d',
        label: '60-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '60d')
    },
    {
        id: 'trailing-90d',
        label: '90-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '90d')
    },
    {
        id: 'trailing-365d',
        label: '365-Day Trailing',
        columns: columnRegistry.filter((col) => col.group === '365d')
    }
];

// Preset configurations
export const columnPresets = {
    basic: new Set([
        'id',
        'entityId',
        'avgTradeProfit',
        'trades',
        'winRate',
        'riskAdjustedScore',
        'profitWeightedScore',
        'profitPercentage'
    ]),
    performance: new Set([
        'id',
        'entityId',
        'optionStrategies',
        'exitLogic',
        'avgTradeProfit',
        'trades',
        'winRate',
        'riskAdjustedScore',
        'profitWeightedScore',
        'profit',
        'profitPercentage',
        'trailingStats.avgGain30',
        'trailingStats.numTrades30',
        'trailingStats.winRate30',
        'trailingStats.avgGain60',
        'trailingStats.numTrades60',
        'trailingStats.winRate60'
    ]),
    full: new Set(columnRegistry.map((col) => col.id))
};

// Get default visible columns
export const getDefaultColumns = (): Set<string> => {
    return new Set(columnRegistry.filter((col) => col.defaultVisible).map((col) => col.id));
};

// Create column definitions based on visible columns
interface ColumnDefinitionParams {
    visibleColumns: Set<string>;
    entities: Entity[];
    tradingStrategies: TradingStrategy[];
    optionBuyingStrategies: OptionBuyingStrategy[];
    hasTradeDetails: boolean;
}

const toSharedBacktestScoreInput = (backtest: TradingStrategyBacktest): SharedTradingStrategyBacktest => ({
    ...backtest,
    strategyVersionKey: backtest.strategyVersionKey ?? ''
});

export const createColumnDefinitions = ({
    visibleColumns,
    entities,
    tradingStrategies,
    optionBuyingStrategies,
    hasTradeDetails
}: ColumnDefinitionParams): ColumnDef<TradingStrategyBacktest>[] => {
    const columns: ColumnDef<TradingStrategyBacktest>[] = [];

    if (visibleColumns.has('id')) {
        columns.push({
            accessorKey: 'id',
            header: 'Backtest ID',
            minSize: 80,
            size: 160,
            maxSize: 200,
            cell: (info) => {
                const value = info.getValue<number | string>();
                if (value === null || value === undefined) return null;
                const id = String(value);

                return (
                    <div className="flex flex-col gap-1">
                        <Link className="text-blue-400 hover:underline" to={`/tools/backtestTrades?backtestId=${id}`}>
                            {id} (trades)
                        </Link>
                        <Link
                            className="text-xs text-zinc-300 hover:text-blue-200 hover:underline"
                            to={`/tools/backtestSummary?backtestId=${id}`}
                        >
                            Summary view
                        </Link>
                    </div>
                );
            }
        });
    }

    if (visibleColumns.has('entityId')) {
        columns.push({
            accessorKey: 'entityId',
            header: 'Entity',
            minSize: 150,
            size: 200,
            maxSize: 300,
            cell: (info) => {
                const backtest = info.row.original;
                const entity = entities.find((e) => e.id === backtest.entityId);
                if (!entity) return null;
                return <EntityLink showName showSymbol entity={entity} />;
            }
        });
    }

    if (visibleColumns.has('strategyId')) {
        columns.push({
            accessorKey: 'strategyId',
            header: 'Strategy ID',
            size: 100
        });
    }

    if (visibleColumns.has('startDateTime')) {
        columns.push({
            accessorKey: 'startDateTime',
            header: 'Start Date',
            size: 150,
            cell: (info) => new Date(info.getValue() as string).toLocaleDateString()
        });
    }

    if (visibleColumns.has('endDateTime')) {
        columns.push({
            accessorKey: 'endDateTime',
            header: 'End Date',
            size: 150,
            cell: (info) => new Date(info.getValue() as string).toLocaleDateString()
        });
    }

    if (visibleColumns.has('optionStrategies')) {
        columns.push({
            accessorKey: 'optionStrategies',
            header: 'Option Strategies',
            minSize: 200,
            size: 300,
            maxSize: 500,
            cell: (info) => {
                const backtest = info.row.original;

                const optionBuyingStrategyIds = [
                    backtest.optionBuyingStrategyIdLeg1,
                    backtest.optionBuyingStrategyIdLeg2,
                    backtest.optionBuyingStrategyIdLeg3,
                    backtest.optionBuyingStrategyIdLeg4,
                    backtest.optionBuyingStrategyIdLeg5,
                    backtest.optionBuyingStrategyIdLeg6
                ].filter(Boolean);

                const strategies = optionBuyingStrategyIds.map((id) => optionBuyingStrategies.find((s) => s.id === id)).filter(Boolean);

                if (strategies.length === 0) return null;

                return (
                    <div className="text-xs">
                        {strategies.map((strategy, index) => (
                            <div key={strategy?.id}>
                                {index > 0 && ', '}
                                {strategy?.name}
                            </div>
                        ))}
                    </div>
                );
            }
        });
    }

    if (visibleColumns.has('exitLogic')) {
        columns.push({
            accessorKey: 'exitLogic',
            header: 'Trade Logic V2 Exit',
            minSize: 250,
            size: 350,
            maxSize: 600,
            cell: (info) => {
                const backtest = info.row.original;
                const strategy = tradingStrategies.find((s) => s.id === backtest.strategyId);
                return (
                    <TradeLogicV2ExitSummary
                        className="mb-0 bg-transparent p-0"
                        exitCandidateHash={backtest.exitCandidateHash ?? null}
                        exitCandidateId={backtest.exitCandidateId ?? null}
                        strategy={strategy}
                    />
                );
            }
        });
    }

    // Performance metrics
    if (visibleColumns.has('avgTradeProfit')) {
        columns.push({
            accessorKey: 'avgTradeProfit',
            header: 'Avg Return %',
            size: 120,
            cell: (info) => info.getValue() || ''
        });
    }

    if (visibleColumns.has('trades')) {
        columns.push({
            accessorKey: 'trades',
            header: 'Num Trades',
            size: 100,
            cell: (info) => {
                const backtest = info.row.original;
                // Prefer trailingStats.numTrades when available, fall back to aggregate field
                const trailingNumTrades = backtest.trailingStats?.numTrades;
                return (trailingNumTrades !== undefined ? trailingNumTrades : backtest.numTrades) ?? 0;
            }
        });
    }

    if (visibleColumns.has('winRate')) {
        columns.push({
            accessorKey: 'winRate',
            header: 'Win Rate',
            size: 100,
            cell: (info) => info.getValue() || ''
        });
    }

    if (visibleColumns.has('riskAdjustedScore') && hasTradeDetails) {
        columns.push({
            id: 'riskAdjustedScore',
            header: 'Risk Score',
            size: 100,
            accessorFn: (row) => calculateRiskAdjustedScore(toSharedBacktestScoreInput(row)),
            cell: (info) => {
                const score = info.getValue() as number;

                const colorClass =
                    score >= 2 ? 'text-green-600' : score >= 1.5 ? 'text-blue-600' : score >= 1 ? 'text-gray-600' : 'text-red-600';

                return <span className={colorClass}>{score.toFixed(2)}</span>;
            }
        });
    }

    if (visibleColumns.has('profitWeightedScore') && hasTradeDetails) {
        columns.push({
            id: 'profitWeightedScore',
            header: 'Profit Score',
            size: 100,
            accessorFn: (row) => calculateProfitWeightedScore(toSharedBacktestScoreInput(row)),
            cell: (info) => {
                const score = info.getValue() as number;

                const colorClass =
                    score >= 2 ? 'text-green-600' : score >= 1.5 ? 'text-blue-600' : score >= 1 ? 'text-gray-600' : 'text-red-600';

                return <span className={colorClass}>{score.toFixed(2)}</span>;
            }
        });
    }

    if (visibleColumns.has('profit')) {
        columns.push({
            accessorKey: 'profit',
            header: 'Profit',
            size: 120,
            cell: (info) => info.getValue() || ''
        });
    }

    if (visibleColumns.has('profitPercentage')) {
        columns.push({
            id: 'profitPercentage',
            header: 'Profit %',
            size: 100,
            // Map to percentageGain field from backtest stats, which is stored on the row
            accessorFn: (row) => row.percentageGain,
            cell: (info) => info.getValue() ?? ''
        });
    }

    if (visibleColumns.has('medianProfitPercent')) {
        columns.push({
            accessorKey: 'medianProfitPercent',
            header: 'Median trade %',
            size: 130,
            cell: (info) => {
                const value = info.getValue() as number;
                if (value === undefined || value === null) return '';

                const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                return <span className={colorClass}>{value.toFixed(2)}%</span>;
            }
        });
    }

    if (visibleColumns.has('medianProfitPerDayPercent')) {
        columns.push({
            accessorKey: 'medianProfitPerDayPercent',
            header: 'Median profit/day %',
            size: 160,
            cell: (info) => {
                const value = info.getValue() as number;
                if (value === undefined || value === null) return '';

                const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                return <span className={colorClass}>{value.toFixed(2)}%</span>;
            }
        });
    }

    if (visibleColumns.has('avgProfitPerDayPercent')) {
        columns.push({
            accessorKey: 'avgProfitPerDayPercent',
            header: 'Avg profit/day %',
            size: 140,
            cell: (info) => {
                const value = info.getValue() as number;
                if (value === undefined || value === null) return '';

                const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                return <span className={colorClass}>{value.toFixed(2)}%</span>;
            }
        });
    }

    if (visibleColumns.has('returnOnRiskCapital')) {
        columns.push({
            accessorKey: 'returnOnRiskCapital',
            header: 'Return on risk %',
            size: 150,
            cell: (info) => {
                const value = info.getValue() as number;
                if (value === undefined || value === null) return '';

                const colorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : '';
                return <span className={colorClass}>{value.toFixed(2)}%</span>;
            }
        });
    }

    if (visibleColumns.has('profitFactor')) {
        columns.push({
            accessorKey: 'profitFactor',
            header: 'Profit Factor',
            size: 130,
            cell: (info) => {
                const value = info.getValue() as number;
                if (value === undefined || value === null) return '';

                const colorClass = value >= 1 ? 'text-profit-green !text-[#29a740]' : 'text-red-500';
                return <span className={colorClass}>{value.toFixed(2)}</span>;
            }
        });
    }

    if (visibleColumns.has('distinctTradingDays')) {
        columns.push({
            accessorKey: 'distinctTradingDays',
            header: 'Trading Days',
            size: 120,
            cell: (info) => info.getValue() || ''
        });
    }

    if (visibleColumns.has('activeMonths')) {
        columns.push({
            accessorKey: 'activeMonths',
            header: 'Active Months',
            size: 120,
            cell: (info) => info.getValue() || ''
        });
    }

    if (visibleColumns.has('worstMonth')) {
        columns.push({
            accessorKey: 'worstMonth',
            header: 'Worst Month',
            size: 120,
            cell: (info) => info.getValue() || ''
        });
    }

    // Trailing stats - all periods
    const periods = ['7', '14', '30', '60', '90', '365'];

    periods.forEach((period) => {
        if (visibleColumns.has(`trailingStats.avgGain${period}`)) {
            columns.push({
                accessorKey: `trailingStats.avgGain${period}`,
                header: `Avg Profit ${period}d`,
                size: 120,
                cell: (info) => info.row.original.trailingStats?.[`avgGain${period}`] || ''
            });
        }

        if (visibleColumns.has(`trailingStats.numTrades${period}`)) {
            columns.push({
                accessorKey: `trailingStats.numTrades${period}`,
                header: `Num Trades ${period}d`,
                size: 120,
                cell: (info) => info.row.original.trailingStats?.[`numTrades${period}`] || ''
            });
        }

        if (visibleColumns.has(`trailingStats.winRate${period}`)) {
            columns.push({
                accessorKey: `trailingStats.winRate${period}`,
                header: `Win Rate ${period}d`,
                size: 110,
                cell: (info) => info.row.original.trailingStats?.[`winRate${period}`] || ''
            });
        }
    });

    return columns;
};
